import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  ArrowLeft,
  Bell,
  Building2,
  CreditCard,
  Database,
  List,
  Mail,
  Plug,
  Search,
  Shapes,
  SlidersHorizontal,
  Sparkles,
  Tag,
  User,
  Users,
  Zap,
} from 'lucide-react'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import type { ElementType, KeyboardEvent as ReactKeyboardEvent } from 'react'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'
import useLoginStore from '@/store/LoginStore'
import { useHasPermission } from '@/lib/hooks/useWorkspaceRole'
import { WORKSPACE_PERMISSIONS } from '@/lib/types/roles'
import { useWorkspaceMode } from '@/hooks/useWorkspaceMode'
import { FEATURE_FLAGS } from '@/lib/constants/featureFlags'
import { useCurrentPlan } from '@/lib/db/rxdb/hooks/entities/subscriptions'
import { cn } from '@/lib/utils'

const normalizePath = (path: string) => {
  if (!path) return path
  if (path === '/') return path
  return path.replace(/\/+$/, '')
}

type NavItem = {
  title: string
  url: string
  icon: ElementType
  badge?: string
}

/** True when an overlay (dialog, popover, menu) should own the Escape key. */
function overlayIsOpen(): boolean {
  return Boolean(
    document.querySelector(
      '[role="dialog"], [role="alertdialog"], [role="menu"], [role="listbox"], [data-radix-popper-content-wrapper]',
    ),
  )
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  )
}

export function SettingsSidebar() {
  const { t } = useTranslation('settings')
  const navigate = useNavigate()
  const workspaceSlug = useLoginStore((state) => state.workspaceSlug)
  const workspaceName = useLoginStore(
    (state) =>
      state.workspaces.find((w) => w.workspaceSlug === state.workspaceSlug)?.name,
  )

  const currentPathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const normalizedCurrentPath = normalizePath(currentPathname)

  const canManageWorkspaceSettings = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_WORKSPACE_SETTINGS)
  const canManageMembers = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_MEMBERS)
  const canManageSubscription = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_SUBSCRIPTION)
  const canManageAiSettings = useHasPermission(WORKSPACE_PERMISSIONS.MANAGE_AI_SETTINGS)
  const { isTeam } = useWorkspaceMode()
  const currentPlan = useCurrentPlan()

  // Same gate as before the redesign: dev tools only in dev builds.
  const isDev = import.meta.env.DEV

  // On phones the settings nav is a sheet: put it away after navigating.
  const { isMobile, setOpenMobile } = useSidebar()
  useEffect(() => {
    if (isMobile) setOpenMobile(false)
  }, [normalizedCurrentPath, isMobile, setOpenMobile])

  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)

  const isActive = (path: string) =>
    normalizePath(path) === normalizedCurrentPath

  const accountItems = useMemo<Array<NavItem>>(
    () => [
      { title: t('sidebar.items.profile'), url: `/${workspaceSlug}/settings`, icon: User },
      { title: t('sidebar.items.preferences'), url: `/${workspaceSlug}/settings/preferences`, icon: SlidersHorizontal },
      { title: t('sidebar.items.notifications'), url: `/${workspaceSlug}/settings/notifications`, icon: Bell },
      { title: t('sidebar.items.integrations'), url: `/${workspaceSlug}/settings/integrations`, icon: Plug },
    ],
    [workspaceSlug, t],
  )

  const workspaceItems = useMemo<Array<NavItem>>(
    () =>
      [
        canManageWorkspaceSettings && {
          title: t('sidebar.items.general'),
          url: `/${workspaceSlug}/settings/workspace`,
          icon: Building2,
        },
        isTeam && canManageMembers && {
          title: t('sidebar.items.members'),
          url: `/${workspaceSlug}/settings/members`,
          icon: Users,
        },
        {
          title: t('sidebar.items.labels'),
          url: `/${workspaceSlug}/settings/labels`,
          icon: Tag,
        },
        isTeam && {
          title: t('sidebar.items.statuses'),
          url: `/${workspaceSlug}/settings/statuses`,
          icon: List,
        },
        FEATURE_FLAGS.ENABLE_WORK_ITEM_TYPES && isTeam && {
          title: t('sidebar.items.workItemTypes'),
          url: `/${workspaceSlug}/settings/work-item-types`,
          icon: Shapes,
        },
        FEATURE_FLAGS.ENABLE_CUSTOM_FIELDS && isTeam && {
          title: t('sidebar.items.customFields'),
          url: `/${workspaceSlug}/settings/custom-fields`,
          icon: SlidersHorizontal,
        },
        // AI Settings — admin-gated by MANAGE_AI_SETTINGS. Shown regardless of the
        // current AiEnabled state: this page hosts the workspace AI kill-switch itself,
        // so gating it on aiAvailable would be a chicken-and-egg (admins could never turn
        // AI on from the UI). The page's own controls gate any AI-dependent actions.
        canManageAiSettings && {
          title: t('sidebar.items.ai'),
          url: `/${workspaceSlug}/settings/ai`,
          icon: Sparkles,
        },
        // Gated on ENABLE_SUBSCRIPTIONS so the entry follows the same global
        // kill-switch as the rest of the payment UI; admin-gated by
        // MANAGE_SUBSCRIPTION. The badge names the current plan.
        FEATURE_FLAGS.ENABLE_SUBSCRIPTIONS && canManageSubscription && {
          title: t('sidebar.items.planAndBilling'),
          url: `/${workspaceSlug}/settings/subscription`,
          icon: CreditCard,
          badge: currentPlan?.name,
        },
      ].filter(Boolean) as Array<NavItem>,
    [workspaceSlug, canManageWorkspaceSettings, canManageMembers, canManageSubscription, canManageAiSettings, isTeam, currentPlan?.name, t],
  )

  const devItems = useMemo<Array<NavItem>>(
    () => [
      { title: t('sidebar.items.database'), url: `/${workspaceSlug}/settings/database`, icon: Database },
      { title: t('sidebar.items.subscriptionTesting'), url: `/${workspaceSlug}/settings/dev-subscription`, icon: Zap },
      { title: t('sidebar.items.emailTemplates'), url: `/${workspaceSlug}/settings/templates`, icon: Mail },
    ],
    [workspaceSlug, t],
  )

  const needle = query.trim().toLowerCase()
  const matches = (item: NavItem) => !needle || item.title.toLowerCase().includes(needle)
  const groups = [
    { key: 'account', label: t('shell.accountGroup'), items: accountItems.filter(matches) },
    {
      key: 'workspace',
      label: workspaceName
        ? t('shell.workspaceGroup', { name: workspaceName })
        : t('shell.workspaceGroupFallback'),
      items: workspaceItems.filter(matches),
    },
  ]
  const devMatches = isDev ? devItems.filter(matches) : []
  const firstMatch = [...groups.flatMap((g) => g.items), ...devMatches][0]
  const noMatches = needle.length > 0 && !firstMatch

  // Esc returns to the app — unless an overlay or a text field owns the key.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return
      if (isEditableTarget(e.target) || overlayIsOpen()) return
      void navigate({ to: '/$workspaceSlug/tasks', params: { workspaceSlug } })
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [navigate, workspaceSlug])

  const onSearchKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && firstMatch) {
      e.preventDefault()
      void navigate({ to: firstMatch.url })
      setQuery('')
    } else if (e.key === 'Escape') {
      // First Esc clears the filter, the next one leaves the field.
      e.preventDefault()
      if (query) setQuery('')
      else searchRef.current?.blur()
    }
  }

  const renderItem = (item: NavItem) => {
    const active = isActive(item.url)
    return (
      <SidebarMenuItem key={item.url}>
        <SidebarMenuButton
          asChild
          isActive={active}
          className={cn(
            'h-8 rounded-lg px-2.5 text-[13.5px] data-[active=true]:bg-sidebar-active data-[active=true]:shadow-xs data-[active=true]:ring-1 data-[active=true]:ring-sidebar-border',
          )}
        >
          <Link to={item.url} aria-current={active ? 'page' : undefined}>
            <item.icon className="text-muted-foreground" />
            <span className="flex-1 truncate">{item.title}</span>
            {item.badge && (
              <span className="ml-auto shrink-0 rounded-full border px-1.5 py-px text-[10.5px] font-medium text-muted-foreground">
                {item.badge}
              </span>
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    )
  }

  return (
    <div className="flex min-h-full flex-1 flex-col px-2 pt-2">
      {/* Back to app + Esc hint */}
      <SidebarGroup className="px-0 pb-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="h-8 rounded-lg px-2.5 text-[13.5px]">
              <Link to="/$workspaceSlug/tasks" params={{ workspaceSlug }}>
                <ArrowLeft className="text-muted-foreground" />
                <span className="flex-1">{t('sidebar.backToApp')}</span>
                <kbd className="ml-auto font-sans text-[11px] text-muted-foreground">
                  {t('shell.escHint')}
                </kbd>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarGroup>

      {/* Search filters the nav items below */}
      <div className="relative px-0.5 pb-2">
        <Search
          aria-hidden
          className="pointer-events-none absolute top-1/2 left-3 size-3.5 -translate-y-1/2 text-muted-foreground"
        />
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onSearchKeyDown}
          placeholder={t('shell.searchPlaceholder')}
          aria-label={t('shell.searchLabel')}
          className="h-8 w-full rounded-lg bg-secondary pr-2 pl-8 text-base text-foreground placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none md:text-[13px] [&::-webkit-search-cancel-button]:hidden"
        />
      </div>

      {noMatches && (
        <p className="px-2.5 py-2 text-xs text-muted-foreground" role="status">
          {t('shell.noMatches', { query: query.trim() })}
        </p>
      )}

      {groups.map((group) =>
        group.items.length > 0 ? (
          <SidebarGroup key={group.key} className="px-0 py-1.5">
            <p className="truncate px-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
              {group.label}
            </p>
            <SidebarMenu className="gap-0.5">{group.items.map(renderItem)}</SidebarMenu>
          </SidebarGroup>
        ) : null,
      )}

      {/* Developer tools — dev builds only, pinned to the bottom */}
      {devMatches.length > 0 && (
        <SidebarGroup className="mt-auto border-t border-dashed px-0 pt-3 pb-2">
          <p className="truncate px-2.5 pb-1 text-[10.5px] font-semibold tracking-[0.08em] text-muted-foreground uppercase">
            {t('shell.developerGroup')}
            <span className="font-normal tracking-normal normal-case">
              {' · '}
              {t('shell.developerHint')}
            </span>
          </p>
          <SidebarMenu className="gap-0.5">{devMatches.map(renderItem)}</SidebarMenu>
        </SidebarGroup>
      )}
    </div>
  )
}

export default SettingsSidebar
